import React, {useEffect, useState} from 'react'
import {makeStyles} from "@material-ui/styles";
import {useDispatch, useSelector} from "react-redux";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
    archive,
    attachedFiles, clearBufferFiles,
    copyToBufferFiles,
    createNewFile,
    createNewFolder,
    cutToBufferFiles,
    deleteItems,
    duplicateItem,
    emptyDir,
    getFilesList,
    getFoldersList,
    inverseSelectedFiles,
    listViewChange,
    pasteFiles,
    renameFiles,
    selectAllFiles,
    setHistoryIndex,
    setSelectedFiles,
    setSelectedFolder,
    unsetSelectedFiles,
    unzip
} from "redux/actions/fileManager";
import {Box, Collapse, Grid, Link, Paper} from "@material-ui/core";
import {convertDate, formatBytes} from "helpers/fileManager";
import formats from 'components/fileManager/Elements/formats.json'
import {SERVER_PATH} from "assets/constants";
import {setFileManagerDialog} from "redux/actions/inbox";
import PopupDialog from './Elements/PopupDialog'
import TopBar from "./components/Topbar";
import FolderBarList from "./components/FolderBarList";
import ContainerBar from "./components/ContainerBar";

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
    },
    folderSide: {
        flexGrow: 1,
        background: '#f9fafc',
        borderRight: '1px solid #E9eef9'
    },
    paper: {
        padding: theme.spacing(2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    fmMinimized: {},
    fmExpanded: {
        position: "fixed",
        top: '0',
        left: '0',
        height: '100%',
        width: '100%',
        zIndex: '999',
        padding: '20px',
        background: 'rgba(255, 255, 255, 0.7)'
    },
    containerWrapper: {
        position: 'relative'
    },
    infoMessages: {
        position: 'absolute',
        width: '100%',
        bottom: '0',
        left: '0',
        padding: '10px 20px',
        fontSize: '13px',
        background: '#fff',
        textAlign: 'center',
        borderTop: '1px solid #E9eef9'
    },
}));

const FileManager = ({height, selectCallback}) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const {
        selectedFiles,
        selectedFolder,
        bufferedItems,
        foldersList,
        filesList,
        itemsView,
        history
    } = useSelector(store => store.fileManager);

    const [expand, setExpand] = useState(false);
    const [popupData, setPopup] = useState({open: false});
    const [isLoading, setLoading] = useState(false);
    const [uploadBox, setUploadBox] = useState(false);
    const [messagesList, setMessages] = useState([]);

    //Constants
    const bigHeight = `${window.innerHeight - 100}px`;
    const selectMessages = selectedFiles.length > 0 || bufferedItems.files.length > 0;
    const _height = (height !== undefined || height > 300 ? `${height}px` : '300px');

    useEffect(() => {
        setLoading(true);
        setMessages([{
            title: `Loading Please Wait`,
            type: 'info',
            message: '',
            progress: true,
            disableClose: true
        }]);
        dispatch(getFilesList(''));
        dispatch(getFoldersList()).then(result => {
            dispatch(setSelectedFolder(result.data.path));
            setLoading(false);
            setMessages([]);
        });
    }, [dispatch]);

    //Methods
    const handlingHistory = (historyInfo, index) => {
        dispatch(setHistoryIndex(index));
        unsetSelectedFiles();
        switch (historyInfo.action) {
            case 'folderChange':
                operations.handleSetMainFolder(historyInfo.path, true);
                break;

            default:
                break;
        }
    };

    const handleClickPopupOpen = (data) => {
        setPopup({
            ...data,
            open: true,
        });
    };

    const handleClose = () => {
        setPopup({
            open: false
        });
    };

    const checkSelectedFileType = (type) => {
        try {
            switch (type) {
                case 'text':
                    return formats.textFiles.includes(selectedFiles[0].extension);
                case 'archive':
                    return formats.archiveFiles.includes(selectedFiles[0].extension);

                case 'image':
                    return formats.imageFiles.includes(selectedFiles[0].extension);

                case 'file':
                    return selectedFiles[0].type === 'file';

                default:
                    return false;
            }
        } catch (error) {
            return false;
        }
    };

    //Options
    const operations = {
        handleAddSelected: (path) => {
            dispatch(setSelectedFiles(path));
        },

        handleUnsetSelected: () => {
            dispatch(unsetSelectedFiles());
        },

        handleInverseSelected: () => {
            dispatch(inverseSelectedFiles());
        },

        handleSelectAll: () => {
            dispatch(selectAllFiles());
        },

        handleGotoParent: () => {
            dispatch(unsetSelectedFiles());
            operations.handleSetMainFolder(foldersList.path);
        },

        handleGoBackWard: () => {
            let historyIndex = history.currentIndex > 0 ? history.currentIndex - 1 : 0;
            let historyInfo = history.steps[historyIndex];
            handlingHistory(historyInfo, historyIndex);
        },

        handleGoForWard: () => {
            if (history.currentIndex + 1 < history.steps.length) {
                let historyIndex = history.currentIndex + 1;
                let historyInfo = history.steps[historyIndex];
                handlingHistory(historyInfo, historyIndex);
            }
        },

        handleCopy: () => {
            dispatch(copyToBufferFiles());
            setMessages([{
                title: `File Successfully Copied`,
                type: 'info',
                message: 'You can paste it in any folder',
                timer: 3000,
            }]);
        },

        handleCut: () => {
            dispatch(cutToBufferFiles());
            setMessages([{
                title: `File Successfully Cut`,
                type: 'info',
                message: 'You can paste it in any folder',
                timer: 3000,
            }]);
        },

        handlePaste: () => {
            let files = bufferedItems.files.map((item) => item.path);

            dispatch(pasteFiles(files, bufferedItems.type, selectedFolder)).then(() => {
                // Param -> "Result"
                operations.handleReload();
                dispatch(unsetSelectedFiles());
                setMessages([{
                    title: `File Successfully Pasted`,
                    type: 'success',
                    message: 'You can paste it in any folder',
                    timer: 3000,
                }]);
            }).catch((error) => {
                setMessages([{
                    title: `Error happened while paste items`,
                    type: 'error',
                    message: error.message
                }]);
            });
        },

        handleSetMainFolder: (value, history = false) => {
            dispatch(unsetSelectedFiles());
            dispatch(setSelectedFolder(value, history));
            dispatch(getFilesList(value)).then(() => {
                // Param -> "Result"
                /*setMessages([{
                    title: `File Successfully Loaded`,
                    type: 'success',
                    message: 'You can paste it in any folder',
                    timer: 3000,
                }]);*/
            });

        },

        handleDelete: () => {
            let files = selectedFiles.map((item) => item.path);

            const handleDeleteSubmit = () => {
                setPopup({
                    open: false
                });
                dispatch(deleteItems(files)).then(() => {
                    // Param -> "Result"
                    dispatch(unsetSelectedFiles());
                    operations.handleReload();
                    setMessages([{
                        title: `Delete files and folders request`,
                        type: 'success',
                        message: 'All files and folders successfully deleted'
                    }]);
                }).catch((error) => {
                    setMessages([{
                        title: `Error happened while removing`,
                        type: 'error',
                        message: error.message
                    }]);
                });
            }

            handleClickPopupOpen({
                title: `Deleting selected files and folders: ${selectedFiles.length} items `,
                description: `All selected files and folder will remove without recover`,
                handleClose: handleClose,
                handleSubmit: handleDeleteSubmit,
                nameInputSets: {}
            });
        },

        handleEmptyFolder: () => {
            let path = selectedFolder;

            const handleEmptySubmit = () => {
                setPopup({
                    open: false
                });

                dispatch(emptyDir(path)).then(() => {
                    // Param -> "Result"
                    dispatch(unsetSelectedFiles());
                    operations.handleReload();
                    setMessages([{
                        title: `Empty folder request`,
                        type: 'success',
                        message: 'All files and folders successfully removed'
                    }]);
                }).catch((error) => {
                    setMessages([{
                        title: `Error happened while empty folder`,
                        type: 'error',
                        message: error.message
                    }]);
                });
            }

            handleClickPopupOpen({
                title: `Deleting all files and folders in ${path}`,
                description: `All files and folder will remove without recover`,
                handleClose: handleClose,
                handleSubmit: handleEmptySubmit,
                nameInputSets: {}
            });
        },

        handleNewFile: () => {
            let fileName = 'new_file.txt';
            const handleNewFileChange = value => {
                fileName = value;
            }
            const handleNewFileSubmit = () => {
                setPopup({
                    open: false
                });
                dispatch(createNewFile(selectedFolder, fileName)).then(() => {
                    // Param -> "Result"
                    operations.handleReload();
                }).catch((error) => {
                    setMessages([{
                        title: `Already Exist`,
                        type: 'error',
                        message: error.message
                    }]);
                });
            }

            handleClickPopupOpen({
                title: `Creating new file`,
                description: 'Only allowed file extensions can be created. Otherwise will be ignored by server.',
                handleClose: handleClose,
                handleSubmit: handleNewFileSubmit,
                nameInputSets: {
                    label: 'File Name',
                    value: fileName,
                    callBack: handleNewFileChange,
                }
            });
        },

        handleNewFolder: () => {
            let folderName = 'folder';
            const handleNewFolderChange = value => {
                folderName = value;
            }
            const handleNewFolderSubmit = () => {
                setPopup({
                    open: false
                });
                dispatch(createNewFolder(selectedFolder, folderName)).then(() => {
                    // Param -> "Result"
                    operations.handleReload();
                }).catch((error) => {
                    setMessages([{
                        title: `Error happened while creating folder`,
                        type: 'error',
                        message: error.message
                    }]);
                });
            }

            handleClickPopupOpen({
                title: `Creating new folder`,
                description: 'Dont use spaces, localised symbols or emojis. This can affect problems',
                handleClose: handleClose,
                handleSubmit: handleNewFolderSubmit,
                nameInputSets: {
                    label: 'Folder Name',
                    value: folderName,
                    callBack: handleNewFolderChange,
                }
            });
        },

        handleRename: () => {
            let item = selectedFiles[0];
            let renameTxt = item.name;
            const handleRenameChange = value => {
                renameTxt = value;
            }
            const handleRenameSubmit = () => {
                setPopup({
                    open: false
                });
                dispatch(renameFiles(item.path, renameTxt)).then(() => {
                    // Param -> "Result"
                    dispatch(unsetSelectedFiles());
                    operations.handleReload();
                }).catch((error) => {
                    setMessages([{
                        title: `Error happened while rename`,
                        type: 'error',
                        message: error.message
                    }]);
                });
            }

            handleClickPopupOpen({
                title: `Renaming of ${item.name}`,
                handleClose: handleClose,
                handleSubmit: handleRenameSubmit,
                nameInputSets: {
                    label: 'Folder Name',
                    value: renameTxt,
                    callBack: handleRenameChange,
                }
            });
        },

        handleReload: () => {
            setLoading(true);
            dispatch(unsetSelectedFiles())
            setMessages([{
                title: `Wait While Reloading`,
                type: 'info',
                message: '',
                progress: true,
                disableClose: true
            }]);
            dispatch(getFilesList(selectedFolder));
            dispatch(getFoldersList()).then(() => {
                // Param -> "Result"
                setLoading(false);
                setMessages([]);
            });
        },

        handleDuplicate: () => {
            let item = selectedFiles[0];
            dispatch(duplicateItem(item.path)).then(() => {
                // Param -> "Result"
                dispatch(unsetSelectedFiles());
                operations.handleReload();
            }).catch((error) => {
                setMessages([{
                    title: `Error happened while duplicate`,
                    type: 'error',
                    message: error.message
                }]);
            });
        },

        handleCreateZip: () => {
            let name = 'archive_name';
            let files = selectedFiles.map((item) => item.path);
            let destination = selectedFolder;
            const handleArchiveChange = value => {
                name = value;
            }
            const handleArchiveSubmit = () => {
                setPopup({
                    open: false
                });
                dispatch(archive(files, destination, name)).then(() => {
                    // Param -> "Result"
                    operations.handleReload();
                    dispatch(unsetSelectedFiles());
                }).catch((error) => {
                    setMessages([{
                        title: `Error happened while creating archive`,
                        type: 'error',
                        message: error.message
                    }]);
                });
            }

            handleClickPopupOpen({
                title: `Creating new archive`,
                description: 'Create a new archive with all selected files. If there is already file with this name it will replace',
                handleClose: handleClose,
                handleSubmit: handleArchiveSubmit,
                nameInputSets: {
                    label: 'Archive Name',
                    value: name,
                    callBack: handleArchiveChange,
                }
            });
        },

        handleExtractZip: () => {
            let file = selectedFiles[0].path;
            let destination = selectedFolder;
            const handleArchiveSubmit = () => {
                setPopup({
                    open: false
                });
                dispatch(unzip(file, destination)).then(() => {
                    // Param -> "Result"
                    operations.handleReload();
                    dispatch(unsetSelectedFiles());
                }).catch((error) => {
                    setMessages([{
                        title: `Error happened while extraction archive`,
                        type: 'error',
                        message: error.message
                    }]);
                });
            }

            handleClickPopupOpen({
                title: `Extract all files from archive to ${destination}`,
                description: 'All files will extracted. If they are existed in folder already they will replaced.',
                handleClose: handleClose,
                handleSubmit: handleArchiveSubmit,
                nameInputSets: {}
            });
        },

        handlePreview: () => {
            let file = selectedFiles[0];
            dispatch(unsetSelectedFiles());
            handleClickPopupOpen({
                title: `File: ${file.name}`,
                description: `<img src="${SERVER_PATH}${file.path}" alt="description" />`,
                handleClose: handleClose,
                nameInputSets: {}
            });
        },

        handleGetInfo: () => {
            let file = selectedFiles[0];
            let isImage = checkSelectedFileType('image');
            dispatch(unsetSelectedFiles());
            let description = `
                <ul class="list">
                    <li><b>Name</b> : ${file.name}</li>
                    <li><b>Path</b> : ${file.path}</li>
                    ${file.type === 'file' ?
                `<li><b>Size</b> : ${formatBytes(file.size)}</li>
                    <li><b>Extension</b> : ${file.extension}</li>`
                : ''}
                    <li><b>Created</b> : ${convertDate(file.created)}</li>
                    <li><b>Modified</b> : ${convertDate(file.modified)}</li>
                    <li><b>Permissions</b> : Others - ${file.premissions.others}, Group - ${file.premissions.group}, Owner - ${file.premissions.owner}</li>
                </ul>
                ${isImage ? `<img src="${SERVER_PATH}${file.path}" alt="file_path" />` : ''}
            `;
            handleClickPopupOpen({
                title: `File: ${file.name}`,
                description,
                handleClose: handleClose,
                nameInputSets: {}
            });
        },

        handleReturnCallBack: (item) => {
            console.log('handleReturnCallBack Method Called', item);
            if (selectCallback) {
                selectCallback(item);
            }
            return true;
        },

        handleUpload: () => {
            setUploadBox(!uploadBox);
            setLoading(!isLoading);
        },

        handleEditText: () => {
            console.log('IMage Edit');
        },

        handleDownload: () => {
            let file = selectedFiles[0];
            setTimeout(() => {
                window.open(`${SERVER_PATH}${file.path}`);
            }, 100);
        },

        handleFullExpand: () => {
            setExpand(!expand);
        },

        handleViewChange: (type) => {
            dispatch(listViewChange(type));
        },

        handleDragEnd: (result) => {
            setLoading(!isLoading);
            try {
                let files = [], destination;
                filesList.forEach(file => {
                    if (file.id === result.draggableId) {
                        files = [file.path];
                    }
                    if (file.id === result.destination.droppableId) {
                        destination = file.path;
                    }
                });

                if (destination !== undefined && files.length !== 0) {
                    pasteFiles(files, 'cut', destination).then(() => {
                        // Param -> "Result"
                        operations.handleReload();
                        setMessages([{
                            title: `File Successfully Moved`,
                            type: 'success',
                            message: 'File that you dragged successfully moved',
                            timer: 3000,
                        }]);
                    }).catch((error) => {
                        console.log('ERROR ->', error);
                    });
                }

            } catch (error) {
                console.log('ERROR ->', error);
            }
            setLoading(!isLoading);
            console.log('Drag ended', result);
        },

        handleAttach: () => {
            dispatch(attachedFiles(selectedFiles));
            dispatch(unsetSelectedFiles());
            dispatch(setFileManagerDialog(false));
        }

    }

    const allButtons = {
        copy: {
            title: 'Copy',
            icon: 'icon-copy',
            onClick: operations.handleCopy,
            disable: !(selectedFiles.length > 0)
        },
        cut: {
            title: 'Cut',
            icon: 'icon-scissors',
            onClick: operations.handleCut,
            disable: !(selectedFiles.length > 0)
        },
        paste: {
            title: 'Paste',
            icon: 'icon-paste',
            onClick: operations.handlePaste,
            disable: !(bufferedItems.files.length > 0)
        },
        delete: {
            title: 'Delete',
            icon: 'icon-trash',
            onClick: operations.handleDelete,
            disable: !(selectedFiles.length > 0)
        },
        emptyFolder: {
            title: 'Empty Folder',
            icon: 'icon-delete-folder',
            onClick: operations.handleEmptyFolder,
        },
        rename: {
            title: 'Rename',
            icon: 'icon-text',
            onClick: operations.handleRename,
            disable: !(selectedFiles.length === 1)

        },
        newFile: {
            title: 'Few File',
            icon: 'icon-add',
            onClick: operations.handleNewFile,
        },
        newFolder: {
            title: 'New Folder',
            icon: 'icon-add-folder',
            onClick: operations.handleNewFolder,
        },
        goForward: {
            title: 'Forward',
            icon: 'icon-forward',
            onClick: operations.handleGoForWard,
            disable: !(history.currentIndex + 1 < history.steps.length)
        },
        goParent: {
            title: 'Go to parent folder',
            icon: 'icon-backward',
            onClick: operations.handleGotoParent,
            disable: selectedFolder === foldersList.path
        },
        goBack: {
            title: 'Back',
            icon: 'icon-backward',
            onClick: operations.handleGoBackWard,
            disable: !(history.currentIndex > 0)
        },
        selectAll: {
            title: 'Select all',
            icon: 'icon-add-3',
            onClick: operations.handleSelectAll,
            disable: !(selectedFiles.length !== filesList.length)
        },
        selectNone: {
            title: 'Select none',
            icon: 'icon-cursor',
            onClick: operations.handleUnsetSelected,
            disable: (selectedFiles.length === 0)
        },
        inverseSelected: {
            title: 'Invert selection',
            icon: 'icon-refresh',
            onClick: operations.handleInverseSelected,
            disable: !(selectedFiles.length !== filesList.length && selectedFiles.length > 0)
        },
        selectFile: {
            title: 'Select file',
            icon: 'icon-outbox',
            onClick: operations.handleReturnCallBack,
            disable: typeof selectCallback === 'undefined'
        },
        reload: {
            title: 'Reload',
            icon: 'icon-refresh',
            onClick: operations.handleReload,
        },
        duplicate: {
            title: 'Duplicate',
            icon: 'icon-layers',
            onClick: operations.handleDuplicate,
            disable: !(selectedFiles.length === 1)
        },

        editFile: {
            title: 'Edit File',
            icon: 'icon-pencil',
            onClick: operations.handleEditText,
            disable: !(selectedFiles.length === 1 && checkSelectedFileType('text'))
        },

        editImage: {
            title: 'Resize & Rotate',
            icon: 'icon-paint-palette',
            onClick: operations.handleEditImage,
            disable: !(selectedFiles.length === 1 && checkSelectedFileType('image'))
        },
        createZip: {
            title: 'Create archive',
            icon: 'icon-zip',
            onClick: operations.handleCreateZip,
            disable: !(selectedFiles.length > 0)
        },
        extractZip: {
            title: 'Extract files from archive',
            icon: 'icon-zip-1',
            onClick: operations.handleExtractZip,
            disable: !(selectedFiles.length === 1 && checkSelectedFileType('archive'))
        },
        uploadFile: {
            title: 'Upload Files',
            icon: 'icon-cloud-computing',
            onClick: operations.handleUpload,
        },
        searchFile: {
            title: 'Search File',
            icon: 'icon-search',
            onClick: operations.handleSearchFile,
        },
        saveFile: {
            title: 'Save Changes',
            icon: 'icon-save',
            onClick: operations.handleSaveFileChanges,
        },
        preview: {
            title: 'View',
            icon: 'icon-view',
            onClick: operations.handlePreview,
            disable: !(selectedFiles.length === 1 && checkSelectedFileType('image'))
        },
        getInfo: {
            title: 'Get Info',
            icon: 'icon-information',
            onClick: operations.handleGetInfo,
            disable: !(selectedFiles.length === 1)
        },
        download: {
            title: 'Download File',
            icon: 'icon-download-1',
            onClick: operations.handleDownload,
            disable: !(selectedFiles.length === 1 && checkSelectedFileType('file'))
        },
        gridView: {
            title: 'Grid view',
            icon: 'icon-layout-1',
            onClick: () => operations.handleViewChange('grid'),
            disable: itemsView === 'grid'
        },
        listView: {
            title: 'List View',
            icon: 'icon-layout-2',
            onClick: () => operations.handleViewChange('list'),
            disable: itemsView === 'list'
        },
        fullScreen: {
            title: 'Full Screen',
            icon: expand ? 'icon-minimize' : 'icon-resize',
            onClick: operations.handleFullExpand
        },
        attach: {
            title: 'Attach Files with Message',
            icon: 'icon-tick',
            onClick: operations.handleAttach
        }
    }

    const availableButtons = {
        topbar: [
            [allButtons.goBack, allButtons.goForward, allButtons.goParent],
            [allButtons.newFile, allButtons.newFolder, allButtons.uploadFile, allButtons.reload],
            [allButtons.copy, allButtons.cut, allButtons.paste, allButtons.delete, allButtons.emptyFolder, allButtons.duplicate],
            [allButtons.rename],
            [allButtons.inverseSelected, allButtons.selectNone, allButtons.selectAll],
            [allButtons.createZip, allButtons.extractZip],
            [allButtons.preview, allButtons.getInfo, allButtons.selectFile, allButtons.download],
            [allButtons.gridView, allButtons.listView, allButtons.fullScreen],
            [allButtons.attach]
        ],

        file: [
            [allButtons.copy, allButtons.cut, allButtons.paste, allButtons.delete, allButtons.duplicate],
            [allButtons.rename, allButtons.attach],
            [allButtons.createZip, allButtons.extractZip],
            [allButtons.preview, allButtons.getInfo, allButtons.selectFile, allButtons.download],
        ],
        container: [
            [allButtons.goBack, allButtons.goForward, allButtons.goParent],
            [allButtons.newFile, allButtons.newFolder, allButtons.emptyFolder, allButtons.uploadFile, allButtons.reload],
            [allButtons.inverseSelected, allButtons.selectNone, allButtons.selectAll],
            [allButtons.gridView, allButtons.listView, allButtons.fullScreen],
        ],
    }

    return (
        <div>
            <div className={expand ? classes.fmExpanded : classes.fmMinimized}>
                <Paper>
                    {popupData.open && <PopupDialog {...popupData} />}
                    <TopBar buttons={availableButtons}/>
                    <Grid container>
                        <Grid item xs={3} sm={2} className={classes.folderSide}>
                            <PerfectScrollbar>
                                <div style={{maxHeight: (expand ? bigHeight : _height)}}>
                                    <FolderBarList foldersList={foldersList}
                                                   onFolderClick={operations.handleSetMainFolder}
                                                   selectedFolder={selectedFolder}/>
                                </div>
                            </PerfectScrollbar>
                        </Grid>
                        <Grid className={classes.containerWrapper} item xs={9} sm={10}>
                            <PerfectScrollbar>
                                <div style={{maxHeight: (expand ? bigHeight : _height)}}>
                                    <ContainerBar buttons={availableButtons} messages={messagesList}
                                                  isloading={isLoading} uploadBox={uploadBox} operations={operations}/>
                                </div>
                            </PerfectScrollbar>
                            <Collapse in={selectMessages}>
                                <Box className={classes.infoMessages}>
                                    {selectedFiles.length > 0 &&
                                    <div className="text"><b>{selectedFiles.length}</b> items are selected</div>}
                                    {bufferedItems.files.length > 0 &&
                                    <div className="text">
                                        <b>{bufferedItems.files.length}</b> {bufferedItems.type === 'cut' ? 'cuted' : 'copied'} items
                                        in buffer
                                        (<Link onClick={() => dispatch(clearBufferFiles())}>Clear</Link>)
                                    </div>
                                    }
                                </Box>
                            </Collapse>
                        </Grid>
                    </Grid>
                </Paper>
            </div>
        </div>
    )
};

FileManager.defaultProps = {
    height: 500,
    selectCallback: () => {}
};

export default FileManager;